// FR-ST24: the capped page is a first-class state, not an error. Shown to anonymous/link
// viewers when public delivery is hard-stopped (FR-ST23a). Members never see it (ST26).
// The same message renders inside /embed/ (`embedded` fills the iframe instead of a 16:9 card).
import { useEffect } from "react";
import { Callout, VideoFrame } from "@grackle/ui";

export interface CappedInfo {
  resumes_at: string | null;
  message: string;
}

/** "starts to return at 3:00 PM (in ~4 h)" in the viewer's local time. */
export function resumeText(resumesAt: string | null, now: Date = new Date()): string {
  if (!resumesAt) return "It will start to return once the owner raises the limit.";
  const at = new Date(resumesAt);
  if (Number.isNaN(at.getTime())) return "It will start to return later.";
  const hours = Math.max(1, Math.ceil((at.getTime() - now.getTime()) / 3_600_000));
  const time = at.toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" });
  return `It starts to return at ${time} (in ~${hours} h).`;
}

/** Adds `<meta name="robots" content="noindex">` while mounted. */
export function useNoindex(): void {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    meta.setAttribute("data-grackle-capped", "");
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
}

export default function CappedState({
  title,
  posterUrl,
  capped,
  embedded = false,
}: {
  title: string;
  posterUrl: string | null;
  capped: CappedInfo;
  embedded?: boolean;
}) {
  useNoindex();
  return (
    <VideoFrame
      tone="dark"
      radius={embedded ? 0 : undefined}
      ratio={embedded ? "auto" : undefined}
      className={embedded ? "watch-capped watch-capped--embed" : "watch-capped"}
    >
      {posterUrl ? <img className="watch-capped__poster" src={posterUrl} alt="" aria-hidden /> : null}
      <div className="watch-status" role="status" data-testid="capped-state">
        <div className="watch-status__title">{title}</div>
        <Callout tone="now" icon="hourglass-high">
          This recording has reached its viewing limit. {resumeText(capped.resumes_at)}
        </Callout>
      </div>
    </VideoFrame>
  );
}
